import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Divider,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ArrowForward as ArrowForwardIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  PriceCheck as PriceCheckIcon,
  AccountBalanceWallet as WalletIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { getPricingTier, isWalletWhitelisted, connectToMetaMask } from '../services/paymentService';

// Composant InfoButton qui remplace les Tooltip pour les appareils tactiles
interface InfoButtonProps {
  title: string;
  children?: React.ReactNode;
}

const InfoButton: React.FC<InfoButtonProps> = ({ title, children }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <IconButton 
        size="small" 
        onClick={handleOpen} 
        color="inherit"
        sx={{ 
          width: 24,
          height: 24,
          padding: 0,
          color: 'text.secondary'
        }}
      >
        {children || <HelpIcon fontSize="small" />}
      </IconButton>
      
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Help Information</Typography>
            <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ py: 1 }}>{title}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ConfigSetup: React.FC = () => {
  const { config, updateConfig, setCurrentStep } = useAppContext();
  const [namePrefix, setNamePrefix] = useState(config.namePrefix);
  const [description, setDescription] = useState(config.description);
  const [baseURI, setBaseURI] = useState(config.baseURI || "");
  const [width, setWidth] = useState(config.format.width.toString());
  const [height, setHeight] = useState(config.format.height.toString());
  const [smoothing, setSmoothing] = useState(config.format.smoothing);
  const [growEditionSizeTo, setGrowEditionSizeTo] = useState(
    config.layerConfigurations[0].growEditionSizeTo.toString()
  );
  const [uniqueDnaTorrance, setUniqueDnaTorrance] = useState(
    config.uniqueDnaTorrance.toString()
  );
  const [rarityDelimiter, setRarityDelimiter] = useState(config.rarityDelimiter);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [onlyOneOfOne, setOnlyOneOfOne] = useState(false);
  
  // Calculate current pricing tier based on collection size
  const currentSize = parseInt(growEditionSizeTo) || 0;
  const currentTier = getPricingTier(currentSize);
  const isWhitelisted = isWalletWhitelisted(walletAddress);

  // Connect wallet function
  const handleConnectWallet = async () => {
    try {
      const address = await connectToMetaMask();
      setWalletAddress(address);
    } catch (error) {
      console.error("Error connecting wallet:", error);
    }
  };

  useEffect(() => {
    // Update local state when config changes
    setNamePrefix(config.namePrefix);
    setDescription(config.description);
    setBaseURI(config.baseURI || "");
    setWidth(config.format.width.toString());
    setHeight(config.format.height.toString());
    setSmoothing(config.format.smoothing);
    setGrowEditionSizeTo(config.layerConfigurations[0].growEditionSizeTo.toString());
    setUniqueDnaTorrance(config.uniqueDnaTorrance.toString());
    setRarityDelimiter(config.rarityDelimiter);
  }, [config]);

  const handleNext = () => {
    const updatedConfig = {
      ...config,
      namePrefix,
      description,
      baseURI: baseURI.trim() || undefined,
      format: {
        ...config.format,
        width: parseInt(width),
        height: parseInt(height),
        smoothing
      },
      layerConfigurations: [
        {
          ...config.layerConfigurations[0],
          growEditionSizeTo: parseInt(growEditionSizeTo)
        }
      ],
      uniqueDnaTorrance: parseInt(uniqueDnaTorrance),
      rarityDelimiter
    };

    updateConfig(updatedConfig);
    // Si onlyOneOfOne est true, on passe directement à l'étape 4 (Unique NFTs)
    setCurrentStep(onlyOneOfOne ? 3 : 1);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Collection Configuration
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <InfoIcon color="info" sx={{ mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Click on the <HelpIcon fontSize="small" color="inherit" sx={{ verticalAlign: 'middle', mx: 0.5 }} /> icons for detailed information about each field.
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Collection Details
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Collection Name Prefix"
              value={namePrefix}
              onChange={(e) => setNamePrefix(e.target.value)}
              margin="normal"
              helperText="This will be used as the prefix for each NFT name (e.g. 'MyCollection #1')"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Collection Description"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
              helperText="Describe your collection. This will be included in the metadata."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Base URI (Optional)"
              value={baseURI}
              onChange={(e) => setBaseURI(e.target.value)}
              margin="normal"
              helperText="Optional: IPFS or other URI prefix for metadata image links (e.g. 'ipfs://QmYourCID/'). Will be used in metadata as 'image': 'ipfs://baseURI/#.png'"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InfoButton title="This will be used in the metadata JSON files to create image links like 'ipfs://yourBaseURI/1.png'. This is not necessary when using metadata in CSV format to create a collection on Opensea." />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Image Format
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Width"
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
              }}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Height"
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
              }}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Image Smoothing</InputLabel>
              <Select
                value={smoothing ? "true" : "false"}
                label="Image Smoothing"
                onChange={(e) => setSmoothing(e.target.value === "true")}
              >
                <MenuItem value="true">Enabled</MenuItem>
                <MenuItem value="false">Disabled (for pixel art)</MenuItem>
              </Select>
              <FormHelperText>Enable for better quality, disable for pixel art</FormHelperText>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Collection Size"
              type="number"
              value={growEditionSizeTo}
              onChange={(e) => setGrowEditionSizeTo(e.target.value)}
              margin="normal"
              helperText={
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    Number of NFTs to generate in this collection
                  </Typography>
                  
                  {/* Prix pour les utilisateurs non whitelistés */}
                  {walletAddress && !isWhitelisted && currentTier.price !== "0" && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
                      <Chip
                        icon={<PriceCheckIcon fontSize="small" sx={{ color: 'white' }} />}
                        label={`Price: ${currentTier.price} ETH`}
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{ 
                          fontWeight: 'bold',
                          color: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          '& .MuiChip-label': { 
                            px: 1 
                          }
                        }}
                      />
                    </Box>
                  )}
                  
                  {/* Message pour les utilisateurs whitelistés */}
                  {walletAddress && isWhitelisted && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
                      <Chip
                        label="Free (Whitelisted)"
                        size="small"
                        color="success"
                        variant="filled"
                        sx={{ 
                          fontWeight: 'bold',
                          color: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      />
                    </Box>
                  )}
                  
                  {/* Bouton de connexion du portefeuille */}
                  {!walletAddress && currentTier.price !== "0" && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
                      <Chip
                        icon={<WalletIcon fontSize="small" sx={{ color: 'white' }} />}
                        label="Connect wallet to check price"
                        size="small"
                        color="info"
                        variant="filled"
                        sx={{ 
                          color: 'white',
                          cursor: 'pointer',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          '&:hover': {
                            bgcolor: 'info.dark'
                          }
                        }}
                        onClick={handleConnectWallet}
                      />
                    </Box>
                  )}
                </Box>
              }
              InputProps={{
                inputProps: { min: 1 },
                endAdornment: (
                  <InputAdornment position="end">
                    <InfoButton title="The total number of NFTs that will be generated" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Rarity Delimiter"
              value={rarityDelimiter}
              onChange={(e) => setRarityDelimiter(e.target.value)}
              margin="normal"
              helperText="Character used to separate name and rarity in filenames"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InfoButton title="The character used to separate the name and rarity weight in filenames (e.g. 'trait#10.png' where '#' is the delimiter). The number after the delimiter represents the relative weight - higher numbers mean more common traits. For example, trait#10 has a 10% chance if the total weights in that layer add up to 100. Ideally, weights in each layer should sum to 100 for easy percentage calculation." />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="DNA Uniqueness Tolerance"
              type="number"
              value={uniqueDnaTorrance}
              onChange={(e) => setUniqueDnaTorrance(e.target.value)}
              margin="normal"
              helperText="Maximum retry attempts for duplicate DNA"
              InputProps={{
                inputProps: { min: 0 },
                endAdornment: (
                  <InputAdornment position="end">
                    <InfoButton title="Maximum number of retry attempts when a duplicate DNA is created" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Collection Type
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={onlyOneOfOne}
              onChange={(e) => setOnlyOneOfOne(e.target.checked)}
              color="primary"
            />
          }
          label="Collection only include 1/1 NFTs"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Enable this if your collection will only contain unique one-of-one NFTs. This will skip the layer setup and image upload steps.
        </Typography>
      </Paper>

      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleNext}
          endIcon={<ArrowForwardIcon />}
        >
          {onlyOneOfOne ? "Next: Unique NFTs" : "Next: Layer Setup"}
        </Button>
      </Box>
    </Box>
  );
}; 